import React from "react";
import "katex/dist/katex.min.css";
import { BlockMath, InlineMath } from "react-katex";
import DOMPurify from "dompurify";

const FileUploadPreprocessAndRenderLatex = ({ content, ref, isBlockMath, wrapperStyle }) => {
    // Utility function to replace LaTeX newline characters with HTML line breaks
    const handleLineBreaks = (text) => {
        return text.replace(/\\newline|\\\\/g, '<br/>');
    };

    // Remove all single and double dollar signs used for LaTeX math delimiters
    const removeAllMathDelimiters = (text) => {
          // This regex matches isolated $...$, $$...$$, and variations with spaces or broken patterns
        return text.replace(/\$\$?([\s\S]*?)\$\$?/g, '$1');
    };

    const preprocessContent = (text) => {
        // Remove aligned and array environments completely
        text = text.replace(/\\begin\{aligned\}[\s\S]*?\\end\{aligned\}/g, '');
        text = text.replace(/\\begin\{array\}[\s\S]*?\\end\{array\}/g, '');
        return text;
    };

        // Extracts and formats the numbers from LaTeX formatted mathbf{}
        const formatMathbf = (text) => {
            return text
                    .replace(/mathbf\{(\d+) (\d+)\}/g, '$1$2')
                    .replace(/mathbf\{(\d+)\}/g, '$1')
                    .replace(/\$\s*([^$]+?)\s*\$/g, '$1')
                    .replace(/\$([\s\S]+?)\$/g, '$1') ;
        };

        // Function to convert rows and columns into HTML table format
        const formatTextToTable = (text) => {
            // Split text by newlines to process as rows
            const rows = text.split('\n').map(row => {
                // Assume that columns are separated by at least two spaces
                const columns = row.split(/\s{2,}/);
                return `<tr>${columns.map(col => `<td>${formatMathbf(col)}</td>`).join('')}</tr>`;
            }).join('');
            return `<table>${rows}</table>`;
        };

            const formatTables = (text) => {
                return text
                    .replace(/\\begin\{tabular\}\{[^}]*\}/g, '<table style="border-collapse: collapse; width: 100%;"><tbody>')
                    .replace(/\\end\{tabular\}/g, '</tbody></table>')
                    .replace(/\\\\/g, '</tr><tr>')
                    .replace(/&/g, '</td><td style="border: 1px solid black; padding: 4px;">');
            };


    // Function to render basic HTML with simple tables and line breaks
    const renderFormattedHtml = (htmlString) => {
        // htmlString = removeAllMathDelimiters(htmlString);
        htmlString = preprocessContent(htmlString); 
        htmlString = formatTextToTable(htmlString);
        htmlString = formatTables(htmlString);

        const formattedHtml = htmlString
        .replace(/\\hline/g, '')
        .replace(/\\begin{tabular}{[^}]*}/g, '<table><tbody>')
        .replace(/\\end{tabular}/g, '</tbody></table>')
        .replace(/\\\\/g, '</tr><tr>')
        .replace(/\\textbf{([^}]*)}/g, '<strong>$1</strong>')
        .replace(/\\/g, '')
        .replace(/\n/g, '<br/>')  // Handle new lines
        .replace(/&/g, '</td><td style="border: 1px solid black; padding: 4px;">')

        
        // Parse with DOMParser to avoid XSS risks with dangerouslySetInnerHTML
        const doc = new DOMParser().parseFromString(handleLineBreaks(formattedHtml), "text/html");
        // Security: additionally sanitize with DOMPurify (defense in depth) before
        // injecting via dangerouslySetInnerHTML so any unexpected script/event-handler
        // tags from user-supplied LaTeX are stripped.
        const cleanHtml = DOMPurify.sanitize(doc.body.innerHTML, {
            ALLOWED_TAGS: ['br', 'p', 'b', 'i', 'em', 'strong', 'ul', 'ol', 'li', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'sub', 'sup', 'code', 'pre'],
            ALLOWED_ATTR: ['class', 'style'],
        });
        return <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
    };

    // Function to parse and render the correct math component based on delimiters
    const parseAndRenderLatex = (content) => {
        const parts = [];
        const regex = /(\$\$|\$|\\\[|\\\])/g; // Regex to split on LaTeX delimiters
        let cursor = 0;

        // Preprocess the content to insert line breaks before formulas
         content = content.replace(regex, '$1\\\\');  // Adds a line break after each delimiter

        content.replace(regex, (match, p1, offset) => {
            // Handle the text before the match
            const textSegment = content.slice(cursor, offset);
            if (textSegment) {
                parts.push(renderFormattedHtml(textSegment));
            }
            cursor = offset + match.length; // Move cursor past the current match

            // Determine the type of math based on the delimiter
            if (match === '$' || match === '\\(') { // Inline Math
                const end = content.indexOf((match === '$') ? '$' : '\\)', cursor);
                if (end > -1) {
                    parts.push(<InlineMath key={cursor + 'inline'} math={content.slice(cursor, end)} />);
                    cursor = end + 1; // Move cursor past the closing delimiter
                }
            } else if (match === '$$' || match === '\\[') { // Block Math
                const endDelimiter = (match === '$$') ? '$$' : '\\]';
                const end = content.indexOf(endDelimiter, cursor);
                if (end > -1) {
                    parts.push(<BlockMath key={cursor + 'block'} math={content.slice(cursor, end)} />);
                    cursor = end + endDelimiter.length; // Move cursor past the closing delimiter
                }
            }
        });

        // Handle any remaining text after the last LaTeX block
        if (cursor < content.length) {
            parts.push(renderFormattedHtml(content.slice(cursor)));
        }

        return parts;
    };

    return (
        <div ref={ref} style={wrapperStyle}>
            {parseAndRenderLatex(content)}
        </div>
    );
};

export default FileUploadPreprocessAndRenderLatex;
